import React, { useState, useEffect } from 'react';
import { ordersApi, customersApi, productApi, orgApi, accountsApi } from '../api';
import { invoiceImageService } from '../services/InvoiceImageService';
import { showToast } from '../utils/toast';

const BillingEditor = ({ order, onSave, onCancel }) => {
  // State
  const [billName, setBillName] = useState(order?.name || '');
  const [billItems, setBillItems] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [moneyAccounts, setMoneyAccounts] = useState([]);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ name: '', price: '', unit: 'piece' });
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '' });
  const [selectedMoneyAccountId, setSelectedMoneyAccountId] = useState('');
  const [saving, setSaving] = useState(false);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('amount'); // 'amount' or 'percent'
  const [cashReceived, setCashReceived] = useState('');

  // Initialize data from order
  useEffect(() => {
    if (order) {
      setBillName(order.name || '');
      // Load items with proper structure including discountPercent
      const items = (order.items || []).map((item, idx) => ({
        ...item,
        id: item.id || Date.now() + idx + Math.random(),
        discountPercent: item.discountPercent || 0
      }));
      setBillItems(items);
      setSelectedMoneyAccountId(order.money_account_id || '');
      
      // Set discount
      if (order.discountPercent) {
        setDiscount(order.discountPercent);
        setDiscountType('percent');
      } else if (order.discount) {
        setDiscount(order.discount);
        setDiscountType('amount');
      }

      if (Object.prototype.hasOwnProperty.call(order, 'cashReceived')) {
        setCashReceived(
          order.cashReceived === undefined || order.cashReceived === null
            ? ''
            : String(order.cashReceived)
        );
      } else {
        setCashReceived('');
      }
      
      // Find and set customer
      if (order.customerId && customers.length > 0) {
        const customer = customers.find(c => c.customerId === order.customerId);
        if (customer) {
          setSelectedCustomer(customer);
          setCustomerSearchTerm(customer.name);
        }
      }
    }
  }, [order, customers]);

  // Load data
  const loadData = async () => {
    try {
      const [customersData, productsData, moneyAccountsData, orgData] = await Promise.all([
        customersApi.list(),
        productApi.list(),
        accountsApi.list(),
        orgApi.get()
      ]);
      
      setCustomers(customersData || []);
      setProducts(productsData || []);
      setMoneyAccounts(moneyAccountsData || []);
      
      if (orgData) {
        setOrg(orgData);
      } else {
        setOrg({ 
          name: 'Your Business', 
          address: 'Your Address', 
          phone: 'Your Phone', 
          email: 'your@email.com' 
        });
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Calculate item total with discount (only if overall discount is 0)
  const calculateItemTotal = (item) => {
    const baseTotal = item.price * item.quantity;
    // Only apply item-level discount if overall discount is 0
    if (discount === 0 && item.discountPercent) {
      const itemDiscount = (baseTotal * item.discountPercent) / 100;
      return baseTotal - itemDiscount;
    }
    return baseTotal;
  };

  // Calculate subtotal
  const calculateSubtotal = () => {
    return billItems.reduce((total, item) => total + calculateItemTotal(item), 0);
  };

  // Calculate discount amount
  const calculateDiscountAmount = () => {
    const subtotal = calculateSubtotal();
    if (discountType === 'percent') {
      return (subtotal * discount) / 100;
    }
    return discount || 0;
  };

  // Calculate total
  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discountAmount = calculateDiscountAmount();
    return Math.max(0, subtotal - discountAmount);
  };

  // Add item
  const addItem = (product) => {
    const newItem = {
      id: Date.now() + Math.random(),
      productId: product.productId,
      name: product.name,
      price: product.price,
      quantity: 1,
      unit: product.unit || 'piece',
      discountPercent: product.discountPercent || 0 // Load discount from product
    };
    setBillItems([...billItems, newItem]);
    setProductSearchTerm('');
    setShowProductDropdown(false);
  };

  // Update item
  const updateItem = (id, field, value) => {
    setBillItems(items => 
      items.map(item => 
        item.id === id ? { ...item, [field]: value } : item
      )
    );
  };

  // Remove item
  const removeItem = (id) => {
    setBillItems(items => items.filter(item => item.id !== id));
  };

  // Add new product
  const addNewProduct = async () => {
    if (!newProduct.name.trim()) return;

    try {
      const productData = {
        name: newProduct.name,
        price: parseFloat(newProduct.price) || 0,
        unit: newProduct.unit,
        cost: 0
      };

      const createdProduct = await productApi.create(productData);
      
      // Add to local products list
      setProducts([...products, createdProduct]);
      
      // Add as item
      addItem(createdProduct);
      
      // Reset form
      setNewProduct({ name: '', price: '', unit: 'piece' });
      setShowAddProduct(false);
    } catch (error) {
      console.error('Error creating product:', error);
    }
  };

  // Add new customer
  const addNewCustomer = async () => {
    if (!newCustomer.name.trim()) return;
    
    if (!newCustomer.phone.trim()) {
      showToast({ message: 'Phone number is required', variant: 'warning' });
      return;
    }

    try {
      const customerData = {
        name: newCustomer.name,
        phone: newCustomer.phone,
        address: '',
        email: '',
        customerNumber: ''
      };

      const createdCustomer = await customersApi.create(customerData);
      
      // Add to local customers list
      setCustomers([...customers, createdCustomer]);
      
      // Select the new customer
      setSelectedCustomer(createdCustomer);
      setCustomerSearchTerm(createdCustomer.name);
      
      // Reset form
      setNewCustomer({ name: '', phone: '' });
      setShowAddCustomer(false);
    } catch (error) {
      console.error('Error creating customer:', error);
    }
  };

  // Save order
  const handleSave = async () => {
    if (!billName.trim() || !selectedCustomer) return;

    try {
      setSaving(true);
      
      const orderData = {
        customerId: selectedCustomer.customerId,
        customerName: selectedCustomer.name,
        customerPhone: selectedCustomer.phone,
        customerAddress: selectedCustomer.address || '',
        customerEmail: selectedCustomer.email || '',
        customerNumber: selectedCustomer.customerNumber || '',
        date: order.date?._seconds ? new Date(order.date._seconds * 1000).toISOString().split('T')[0] : order.date,
        items: billItems.map(item => ({
          productId: item.productId,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          unit: item.unit,
          discountPercent: item.discountPercent || 0
        })),
        sellingPrice: calculateTotal(),
        discount: calculateDiscountAmount(),
        discountPercent: discountType === 'percent' ? discount : 0,
        totalCost: order.totalCost || 0,
        source: 'orders',
        money_account_id: selectedMoneyAccountId || null,
        name: billName,
        notes: `Invoice: ${billName}`
      };

      if (cashReceived !== '') {
        orderData.cashReceived = Math.max(0, Number(cashReceived) || 0);
      }

      await ordersApi.update(order.orderId, orderData);
      onSave();
    } catch (error) {
      console.error('Error saving order:', error);
    } finally {
      setSaving(false);
    }
  };

  // Filter customers and products
  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(customerSearchTerm.toLowerCase()) ||
    customer.phone?.toLowerCase().includes(customerSearchTerm.toLowerCase())
  );

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(productSearchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading editor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-xl">
        {/* Header */}
        <div className="bg-green-600 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-lg font-semibold">✏️ Edit Billing Order</h1>
              <p className="text-sm text-green-100">Modify order details and items</p>
            </div>
            <button
              onClick={onCancel}
              className="text-white hover:text-green-200 text-2xl"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-4">
        {/* Bill Name - Read Only */}
        <div className="bg-gray-50 rounded-lg border border-gray-200 p-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">Bill Name</label>
          <div className="w-full px-3 py-2 text-sm bg-gray-100 border border-gray-300 rounded-lg text-gray-600">
            {billName}
          </div>
        </div>

        {/* Customer Selection */}
        <div className="bg-white rounded-lg border border-green-100 p-3">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">Customer</label>
            <button
              onClick={() => setShowAddCustomer(true)}
              className="text-sm text-green-600 hover:text-green-800 font-medium"
            >
              + Add New
            </button>
          </div>
          <div className="relative">
            <input
              type="text"
              value={customerSearchTerm}
              onChange={(e) => {
                setCustomerSearchTerm(e.target.value);
                setShowCustomerDropdown(true);
              }}
              onFocus={() => setShowCustomerDropdown(true)}
              placeholder="Search customer..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
            {showCustomerDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                <div className="p-2">
                  <button
                    onClick={() => {
                      setShowAddCustomer(true);
                      setShowCustomerDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg border-2 border-dashed border-green-300 text-sm"
                  >
                    + Add New Customer
                  </button>
                </div>
                {filteredCustomers.map(customer => (
                  <button
                    key={customer.customerId}
                    onClick={() => {
                      setSelectedCustomer(customer);
                      setCustomerSearchTerm(customer.name);
                      setShowCustomerDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-green-50 border-b border-gray-100 last:border-b-0 text-sm"
                  >
                    <div className="font-medium">{customer.name}</div>
                    <div className="text-xs text-gray-500">{customer.phone}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Product Selection */}
        <div className="bg-white rounded-lg border border-green-100 p-3">
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-gray-700">Add Product</label>
            <button
              onClick={() => setShowAddProduct(true)}
              className="text-sm text-green-600 hover:text-green-800 font-medium"
            >
              + Add New
            </button>
          </div>
          <div className="relative">
            <input
              type="text"
              value={productSearchTerm}
              onChange={(e) => {
                setProductSearchTerm(e.target.value);
                setShowProductDropdown(true);
              }}
              onFocus={() => setShowProductDropdown(true)}
              placeholder="Search product..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
            {showProductDropdown && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                <div className="p-2">
                  <button
                    onClick={() => {
                      setShowAddProduct(true);
                      setShowProductDropdown(false);
                    }}
                    className="w-full text-left px-3 py-2 text-green-600 hover:bg-green-50 rounded-lg border-2 border-dashed border-green-300 text-sm"
                  >
                    + Add New Product
                  </button>
                </div>
                {filteredProducts.map(product => (
                  <button
                    key={product.productId}
                    onClick={() => addItem(product)}
                    className="w-full text-left px-3 py-2 hover:bg-green-50 border-b border-gray-100 last:border-b-0 text-sm"
                  >
                    <div className="font-medium">{product.name}</div>
                    <div className="text-xs text-gray-500">₹{product.price} per {product.unit}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Money Account Selection */}
        <div className="bg-white rounded-lg border border-green-100 p-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">Money Account</label>
          <select
            value={selectedMoneyAccountId}
            onChange={(e) => setSelectedMoneyAccountId(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="">Select Money Account (Optional)</option>
            {moneyAccounts.map(account => (
              <option key={account.accountId} value={account.accountId}>
                {account.name} - ₹{account.balance?.toFixed(2) || '0.00'}
              </option>
            ))}
          </select>
        </div>

        {/* Cash Received */}
        <div className="bg-white rounded-lg border border-green-100 p-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">Cash Received</label>
          <input
            type="number"
            value={cashReceived}
            onChange={(e) => setCashReceived(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Leave blank if fully paid"
            min="0"
            step="0.01"
          />
          <div className="text-[11px] text-gray-500 mt-1">
            Empty field means the bill is fully settled.
          </div>
          {cashReceived !== '' && (
            (() => {
              const totalAmount = calculateTotal();
              const cashValue = Math.max(0, Number(cashReceived) || 0);
              const pendingAmount = Math.max(0, totalAmount - cashValue);
              return (
                <div className="mt-2 p-2 bg-green-50 border border-green-100 rounded-lg text-xs space-y-1">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bill total</span>
                    <span className="font-semibold text-gray-900">₹{totalAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cash received</span>
                    <span className="font-semibold text-green-700">₹{cashValue.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t border-green-100 pt-1">
                    <span className="text-gray-700 font-medium">Pending</span>
                    <span className={`font-semibold ${pendingAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      ₹{pendingAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
              );
            })()
          )}
        </div>

        {/* Discount Section */}
        <div className="bg-white rounded-lg border border-green-100 p-3">
          <label className="block text-sm font-medium text-gray-700 mb-2">Overall Discount</label>
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={discountType}
              onChange={(e) => {
                setDiscountType(e.target.value);
                setDiscount(0);
              }}
              className="w-full sm:w-auto sm:min-w-[140px] px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="amount">Amount (₹)</option>
              <option value="percent">Percent (%)</option>
            </select>
            <input
              type="number"
              value={discount}
              onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
              className="w-full sm:flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder={discountType === 'percent' ? '0' : '0.00'}
              step={discountType === 'percent' ? '1' : '0.01'}
              min="0"
            />
          </div>
          {discount > 0 && (
            <div className="mt-2 text-xs text-green-600">
              Discount: ₹{calculateDiscountAmount().toFixed(2)}
            </div>
          )}
          {discount > 0 && (
            <div className="mt-1 text-xs text-gray-500">
              Note: Overall discount overwrites item-level discounts
            </div>
          )}
        </div>

        {/* Items List */}
        {billItems.length > 0 && (
          <div className="bg-white rounded-lg border border-green-100 p-3">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Items ({billItems.length})</h3>
            <div className="space-y-3">
              {billItems.map((item) => (
                <div key={item.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 text-sm">{item.name}</div>
                      <div className="text-xs text-green-600">per {item.unit}</div>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-red-500 hover:text-red-700 text-lg ml-2"
                    >
                      ✕
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Quantity</label>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => updateItem(item.id, 'quantity', Math.max(0, item.quantity - 1))}
                          className="w-5 h-5 rounded-full bg-red-100 text-red-600 hover:bg-red-200 flex items-center justify-center text-xs font-bold flex-shrink-0"
                        >
                          -
                        </button>
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(item.id, 'quantity', parseFloat(e.target.value) || 0)}
                          className="flex-1 min-w-0 text-center border border-gray-300 rounded px-2 py-1 text-xs font-medium focus:ring-1 focus:ring-green-500 focus:border-green-500"
                          min="0"
                          step="0.01"
                        />
                        <button
                          onClick={() => updateItem(item.id, 'quantity', item.quantity + 1)}
                          className="w-5 h-5 rounded-full bg-green-100 text-green-600 hover:bg-green-200 flex items-center justify-center text-xs font-bold flex-shrink-0"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Price</label>
                      <input
                        type="number"
                        value={item.price}
                        onChange={(e) => updateItem(item.id, 'price', parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:border-green-500"
                        step="0.01"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Disc. %</label>
                      <input
                        type="number"
                        value={item.discountPercent || 0}
                        onChange={(e) => updateItem(item.id, 'discountPercent', parseFloat(e.target.value) || 0)}
                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-green-500 focus:border-green-500"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Total</label>
                      <div className="text-xs font-bold text-green-600 bg-green-50 border border-green-200 rounded px-2 py-1 text-center truncate">
                        ₹{calculateItemTotal(item).toFixed(2)}
                      </div>
                      {item.discountPercent > 0 && discount === 0 && (
                        <div className="text-[10px] text-gray-500 line-through mt-0.5">
                          ₹{(item.price * item.quantity).toFixed(2)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Total */}
            <div className="mt-4 p-3 bg-green-50 rounded-lg">
              <div className="space-y-1">
                {calculateSubtotal() !== calculateTotal() && (
                  <div className="flex justify-between items-center text-xs text-gray-500">
                    <span>Subtotal:</span>
                    <span className="line-through">₹{calculateSubtotal().toFixed(2)}</span>
                  </div>
                )}
                {discount > 0 && (
                  <div className="flex justify-between items-center text-xs text-green-600">
                    <span>Discount:</span>
                    <span>-₹{calculateDiscountAmount().toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-1 border-t border-green-200">
                  <span className="text-sm font-medium text-gray-900">Total:</span>
                  <span className="text-lg font-bold text-green-600">₹{calculateTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={onCancel}
            className="flex-1 bg-gray-500 text-white py-3 px-4 rounded-lg hover:bg-gray-600 transition-colors font-semibold text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!selectedCustomer || saving}
            className="flex-1 bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold text-sm"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
        </div>

        {/* Add Product Modal */}
      {showAddProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add New Product</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({ ...newProduct, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Product name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
                <input
                  type="number"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({ ...newProduct, price: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                <select
                  value={newProduct.unit}
                  onChange={(e) => setNewProduct({ ...newProduct, unit: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="piece">Piece</option>
                  <option value="kg">Kg</option>
                  <option value="liter">Liter</option>
                  <option value="meter">Meter</option>
                  <option value="hour">Hour</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddProduct(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={addNewProduct}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Add Product
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Add New Customer</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={newCustomer.name}
                  onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Customer name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone *</label>
                <input
                  type="tel"
                  value={newCustomer.phone}
                  onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="Phone number"
                  required
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowAddCustomer(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={addNewCustomer}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >
                Add Customer
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default BillingEditor;
